"use client";

import { useEffect, useState, useCallback } from "react";
import { useAuth, getAuthToken } from "@/components/auth-provider";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Bell, Trash2, Plus, Loader2, LogOut, Mail, Zap, TrendingUp, ShieldCheck } from "lucide-react";
import { secureFetch } from "@/lib/security/fetch-client";
import { trackFollow } from "@/lib/analytics";
import { useRouter } from "next/navigation";
import { invalidateFollowCache } from "@/hooks/use-follow-keyword";
import { useAuthDialog } from "@/components/auth-dialog-provider";

interface SubItem {
  keyword: string;
  type: number;
}

/**
 * 订阅管理侧边抽屉。
 * 最新添加排最前，关注/取关后绿色提示(3秒消失)。
 * 整个抽屉可滚动，列表再长也翻得动。
 */
export function SubscriptionDrawer({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { user, loading: authLoading, logout } = useAuth();
  const { openAuth } = useAuthDialog();
  const router = useRouter();
  const [list, setList] = useState<SubItem[]>([]);
  const [newKeyword, setNewKeyword] = useState("");
  const [error, setError] = useState("");
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const [listLoading, setListLoading] = useState(true);

  const token = getAuthToken();
  const authHeaders = (): Record<string, string> => {
    const t = getAuthToken();
    return t ? { Authorization: `Bearer ${t}` } : {};
  };

  const refresh = useCallback(async () => {
    if (!token) return;
    setListLoading(true);
    setError("");
    try {
      const data = await secureFetch<{ subscriptions: SubItem[] }>(
        "/api/subscriptions",
        { method: "GET", headers: { Authorization: `Bearer ${token}` } }
      );
      // 最新添加排最前
      setList((data?.subscriptions || []).reverse());
    } catch {
      setError("加载失败");
    } finally {
      setListLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (open && user) refresh();
  }, [open, user, refresh]);

  // 消息自动消失
  useEffect(() => {
    if (!msg) return;
    const t = setTimeout(() => setMsg(""), 3000);
    return () => clearTimeout(t);
  }, [msg]);

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    const kw = newKeyword.trim();
    if (!kw) return;
    setBusy(true);
    setError("");
    setMsg("");
    try {
      await secureFetch("/api/subscriptions", {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify({ keyword: kw }),
      });
      setNewKeyword("");
      setMsg(`✓ 已订阅「${kw}」`);
      trackFollow(kw, "follow");
      await refresh();
    } catch (err) {
      if (err instanceof Error && err.message.includes("409")) {
        setError("已订阅过该关键词");
      } else {
        setError("添加失败，请重试");
      }
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async (keyword: string) => {
    setError("");
    setMsg("");
    try {
      const res = await fetch(
        `/api/subscriptions?keyword=${encodeURIComponent(keyword)}`,
        { method: "DELETE", headers: authHeaders() }
      );
      if (!res.ok) {
        setError("删除失败");
        return;
      }
      setMsg(`✓ 已取消「${keyword}」`);
      invalidateFollowCache(keyword);
      trackFollow(keyword, "unfollow");
      await refresh();
    } catch {
      setError("网络错误");
    }
  };

  const handleLogout = () => {
    logout();
    onOpenChange(false);
    router.replace("/");
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-[min(24rem,85vw)] sm:w-96 flex flex-col p-0">
        {/* 固定头部 */}
        <SheetHeader className="px-6 pt-6 pb-4 border-b shrink-0">
          <SheetTitle className="flex items-center gap-2">
            <Bell className="w-5 h-5" />
            我的订阅
          </SheetTitle>
          <SheetDescription>
            订阅关键词或关注名人，新动态邮件提醒
          </SheetDescription>
        </SheetHeader>

        {authLoading ? (
          <div className="flex-1 flex items-center justify-center py-12 text-muted-foreground text-sm">
            加载中...
          </div>
        ) : !user ? (
          /* 未登录：功能介绍 + 注册引导 */
          <div className="flex-1 flex flex-col px-6 py-6">
            {/* 功能亮点 */}
            <div className="space-y-4 mb-8">
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                  <Mail className="w-4 h-4 text-primary" />
                </div>
                <div>
                  <h3 className="text-sm font-semibold">邮件实时提醒</h3>
                  <p className="text-xs text-muted-foreground mt-0.5">订阅关键词上热搜时，第一时间邮件通知</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                  <Zap className="w-4 h-4 text-primary" />
                </div>
                <div>
                  <h3 className="text-sm font-semibold">热搜即时追踪</h3>
                  <p className="text-xs text-muted-foreground mt-0.5">关注明星、事件、品牌等关键词，随时掌握热度</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                  <TrendingUp className="w-4 h-4 text-primary" />
                </div>
                <div>
                  <h3 className="text-sm font-semibold">热搜页一键 ★</h3>
                  <p className="text-xs text-muted-foreground mt-0.5">在热搜列表点 ★ 即可订阅，无需手动输入</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                  <ShieldCheck className="w-4 h-4 text-primary" />
                </div>
                <div>
                  <h3 className="text-sm font-semibold">隐私安全</h3>
                  <p className="text-xs text-muted-foreground mt-0.5">订阅数据加密存储，仅用于推送通知</p>
                </div>
              </div>
            </div>

            {/* 注册引导 */}
            <div className="border-t pt-6 mt-auto space-y-2">
              <Button
                className="w-full"
                onClick={() => {
                  onOpenChange(false);
                  openAuth({ mode: "register", title: "免费注册" });
                }}
              >
                免费注册
              </Button>
              <Button
                variant="outline"
                className="w-full"
                onClick={() => {
                  onOpenChange(false);
                  openAuth({ mode: "login", title: "登录" });
                }}
              >
                已有账号？去登录
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex flex-col flex-1 min-h-0">
            {/* 固定操作区 */}
            <div className="px-6 pt-4 pb-2 space-y-3 shrink-0 border-b">
              {/* 用户信息 + 退出 */}
              <div className="flex items-center justify-between rounded-lg border px-3 py-2">
                <div className="min-w-0">
                  <span className="text-sm font-medium truncate block">{user.username}</span>
                  {user.email && (
                    <span className="text-xs text-muted-foreground truncate block">{user.email}</span>
                  )}
                </div>
                <Button variant="ghost" size="sm" onClick={handleLogout} className="text-muted-foreground hover:text-red-500 gap-1 text-xs shrink-0">
                  <LogOut className="w-3.5 h-3.5" />
                  退出
                </Button>
              </div>

              {/* 添加订阅词 */}
              <form onSubmit={handleAdd} className="flex gap-2">
                <Input
                  value={newKeyword}
                  onChange={(e) => setNewKeyword(e.target.value)}
                  placeholder="输入关键词，如 雷军 / 小米"
                  maxLength={50}
                  className="flex-1 h-9 text-sm"
                />
                <Button type="submit" disabled={busy} size="sm" className="gap-1 shrink-0">
                  {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
                  订阅
                </Button>
              </form>
              <p className="text-[11px] text-muted-foreground">
                含该关键词的词条新上热搜时，系统邮件通知
              </p>

              {/* 消息提示 */}
              {error && (
                <p className="text-xs text-red-500 rounded-md bg-red-50 px-3 py-1.5">{error}</p>
              )}
              {msg && (
                <p className="text-xs text-green-600 rounded-md bg-green-50 px-3 py-1.5">{msg}</p>
              )}
            </div>

            {/* 可滚动的列表区 */}
            <div className="flex-1 overflow-y-auto px-6 py-3">
              {listLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                </div>
              ) : list.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Bell className="h-8 w-8 mx-auto mb-2 opacity-30" />
                  <p className="text-sm">还没有订阅</p>
                  <p className="text-xs mt-1">输入关键词或在热搜页点 ★ 即可订阅</p>
                </div>
              ) : (
                <div className="space-y-1">
                  {list.map((item) => (
                    <div
                      key={item.keyword}
                      className="flex items-center justify-between rounded-md border px-3 py-2 group hover:bg-muted/50 transition-colors"
                    >
                      <a
                        href={`/search?q=${encodeURIComponent(item.keyword)}`}
                        className="text-sm hover:text-primary transition-colors truncate flex-1 mr-2"
                      >
                        {item.keyword}
                      </a>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(item.keyword)}
                        className="text-muted-foreground hover:text-red-500 h-7 w-7 p-0 opacity-0 group-hover:opacity-100 transition-opacity shrink-0"
                        title="取消订阅"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  ))}
                  <div className="text-xs text-muted-foreground text-center pt-2 pb-4">
                    共 {list.length} 个订阅
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
